import { alpha } from '@mui/material/styles';

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }

  interface Palette {
    blueBrand: {
      0: string,
      100: string,
      150: string,
      200: string,
      300: string,
      400: string,
      500: string,
    };
    redBrand: {
      300: string,
      400: string,
      500: string,
    };
    redWarning: {
      0: string,
      100: string,
      200: string,
    }
    greenBrand: {
      0: string,
      200: string,
      400: string,
    };
    greenValidation: {
      100: string;
      200: string;
      300: string;
    };
    yellow: {
      0: string,
      100: string,
      150: string,
    };
  }
  interface PaletteOptions {
    blueBrand: {
      0: string,
      100: string,
      150: string,
      200: string,
      300: string,
      400: string,
      500: string,
    };
    redBrand: {
      300: string,
      400: string,
      500: string,
    };
    redWarning: {
      0: string,
      100: string,
      200: string,
    }
    greenBrand: {
      0: string,
      200: string,
      400: string,
    };
    greenValidation: {
      100: string;
      200: string;
      300: string;
    };
    yellow: {
      0: string,
      100: string,
      150: string,
    };
  }
}

// SETUP COLORS

export const grey = {
  0: '#FFFFFF',
  100: '#EDEDED',
  200: '#D9D9D9',
  300: '#BABABA',
  400: '#959595',
  500: '#6E6E6E',
  600: '#251E1E',
  700: '#364657',
  800: '#0D1E30',
  900: '#08131F',
};

export const blueBrand = {
  0: '#EAEEF5',
  100: '#CADAF2',
  150: '#B5DCF0',
  200: '#80ACD6',
  300: '#3069BF',
  400: '#214983',
  500: '#112645',
};

export const redBrand = {
  300: '#D22E2D',
  400: '#931F1F',
  500: '#541212',
};

export const redWarning = {
  0: '#FCEEEE',
  100: '#F3C3C4',
  200: '#FF0000',
};

export const greenBrand = {
  0: '#DFEEC4',
  200: '#A9BF7A',
  400: '#22372B',
};

export const greenValidation = {
  100: '#91A85F',
  200: '#788E4B',
  300: '#627145',
};

export const yellow = {
  0: '#FBE0BE',
  100: '#F6BD74',
  150: '#CB9D62',
};

export const primary = {
  lighter: '#B1E6FF',
  light: '#7CCCF2',
  main: '#214983',
  dark: '#1B4E7E',
  darker: '#251F4E',
  contrastText: '#FFFFFF',
};

export const secondary = {
  lighter: '#D4FFC2',
  light: '#82D660',
  main: '#54A932',
  dark: '#2F7712',
  darker: '#195004',
  contrastText: '#FFFFFF',
};

export const info = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#FFFFFF',
};

export const success = {
  lighter: '#D8FBDE',
  light: '#86E8AB',
  main: '#36B37E',
  dark: '#1B806A',
  darker: '#0A5554',
  contrastText: '#FFFFFF',
};

export const warning = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: '#232A33',
};

export const error = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#FFFFFF',
};

export const common = {
  black: '#000000',
  white: '#FFFFFF',
};

export const action = {
  hover: alpha(grey[500], 0.08),
  selected: alpha(grey[500], 0.16),
  disabled: alpha(grey[500], 0.8),
  disabledBackground: alpha(grey[500], 0.24),
  focus: alpha(grey[500], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
};

const base = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  grey,
  common,
  blueBrand,
  redBrand,
  redWarning,
  greenBrand,
  greenValidation,
  yellow,
  divider: alpha(grey[500], 0.24),
  action,
};

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...base,
    mode: 'light',
    text: {
      primary: grey[800],
      secondary: grey[600],
      disabled: grey[500],
    },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
      neutral: grey[100],
    },
    action: {
      ...base.action,
      active: grey[600],
    },
  };

  const dark = {
    ...base,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: grey[500],
      disabled: grey[600],
    },
    background: {
      paper: grey[800],
      default: grey[900],
      neutral: alpha(grey[500], 0.16),
    },
    action: {
      ...base.action,
      active: grey[500],
    },
  };

  return mode === 'light' ? light : dark;
}
